import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CONTACT_INFO, LOGO_URL } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Fetch services from CMS to populate menu dynamically
    fetch("https://api.dev.auxo.dk/public/content/services")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch((err) => console.error("Error fetching services for menu:", err));
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { label: "Forside", path: "/" },
    { label: "Galleri", path: "/galleri" },
    { label: "Kontakt", path: "/kontakt" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-200 text-neutral-900">
      {/* Top bar with quick contact */}
      <div className="bg-yellow-500 text-neutral-950 py-2 px-4 text-xs font-semibold">
        <div className="max-w-7xl mx-auto flex justify-center items-center">
          <div className="flex items-center gap-4">
            <span className="whitespace-nowrap">
              <i className="fa-solid fa-phone mr-1.5" />
              Tlf: <a href={`tel:${CONTACT_INFO.phone}`} className="hover:underline whitespace-nowrap">{CONTACT_INFO.phoneDisplay}</a>
            </span>
            <span className="hidden sm:inline">|</span>
            <span className="hidden sm:inline whitespace-nowrap">
              <i className="fa-solid fa-envelope mr-1.5" />
              <a href={`mailto:${CONTACT_INFO.email}`} className="hover:underline whitespace-nowrap">{CONTACT_INFO.email}</a>
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2 min-h-[6.5rem]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="JG Mesterbyg - Tømrermester i Erslev på Mors"
              className="h-24 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (Visible on screens >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-yellow-600 ${
                isActive("/") ? "text-yellow-600 border-b-2 border-yellow-600 pb-1" : "text-neutral-700"
              }`}
            >
              Forside
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-yellow-600 flex items-center gap-1.5 cursor-pointer ${
                  isActive("/ydelser") ? "text-yellow-600 border-b-2 border-yellow-600 pb-1" : "text-neutral-700"
                }`}
              >
                Ydelser
                <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute left-0 mt-2 w-72 bg-white border border-neutral-200 rounded-xl shadow-2xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150 before:absolute before:content-[''] before:h-2 before:w-full before:-top-2 before:left-0">
                  {services.length > 0 ? (
                    services.map((service) => (
                      <Link
                        key={service.id}
                        to={`/ydelser/${service.slug._ || service.slug}`}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-yellow-600 transition-all duration-150"
                      >
                        <i className={`fa-solid ${service.icon || "fa-hammer"} text-yellow-500 w-5 text-center`} />
                        <div>
                          <p className="font-semibold">{service.title}</p>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-neutral-500">Henter ydelser...</div>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/galleri"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-yellow-600 ${
                isActive("/galleri") ? "text-yellow-600 border-b-2 border-yellow-600 pb-1" : "text-neutral-700"
              }`}
            >
              Galleri
            </Link>

            <Link
              to="/kontakt"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-yellow-600 ${
                isActive("/kontakt") ? "text-yellow-600 border-b-2 border-yellow-600 pb-1" : "text-neutral-700"
              }`}
            >
              Kontakt
            </Link>
          </nav>

          {/* Desktop CTA Button (Visible on screens >= 1200px) */}
          <div className="hidden min-[1200px]:flex items-center">
            <Link
              to="/fa-tilbud"
              className="bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold px-6 py-2.5 rounded-xl text-sm shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 cursor-pointer"
            >
              <span>Få tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>

          {/* Mobile Burger Button (Visible on screens < 1200px) */}
          <div className="flex min-[1200px]:hidden items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-neutral-700 hover:text-yellow-600 p-2 focus:outline-none cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`fa-solid ${isOpen ? "fa-xmark" : "fa-bars"} text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (Visible on screens < 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden bg-white border-t border-neutral-200 py-4 px-4 space-y-3 animate-in fade-in slide-in-from-top-5 duration-200 shadow-lg">
          <div className="space-y-1">
            <Link
              to="/"
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                isActive("/") ? "bg-yellow-500 text-neutral-950" : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              Forside
            </Link>

            {/* Mobile Services Submenu */}
            <div className="space-y-1">
              <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-neutral-400 mt-2">
                Vores Ydelser
              </div>
              {services.length > 0 ? (
                services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/ydelser/${service.slug._ || service.slug}`}
                    className={`flex items-center gap-3 px-6 py-2 rounded-xl text-sm transition-colors ${
                      isActive(`/ydelser/${service.slug._ || service.slug}`)
                        ? "text-yellow-600 font-bold"
                        : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
                    }`}
                  >
                    <i className={`fa-solid ${service.icon || "fa-hammer"} text-yellow-500 text-xs`} />
                    {service.title}
                  </Link>
                ))
              ) : (
                <div className="px-6 py-2 text-xs text-neutral-500">Henter ydelser...</div>
              )}
            </div>

            <Link
              to="/galleri"
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors mt-2 ${
                isActive("/galleri") ? "bg-yellow-500 text-neutral-950" : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              Galleri
            </Link>

            <Link
              to="/kontakt"
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                isActive("/kontakt") ? "bg-yellow-500 text-neutral-950" : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              Kontakt
            </Link>
          </div>

          <div className="pt-4 border-t border-neutral-200">
            <Link
              to="/fa-tilbud"
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold py-3 px-4 rounded-xl text-center block shadow-md transition-all duration-200"
            >
              Få tilbud
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
